import { Inject } from '@odinjs/odin';
import moment from 'moment';

import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AutocompleteAPI, FetchAPI, FieldAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import {  HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { Value } from '@philips-internal/oswald-ui-core';

import BaseController from '../../../../controllers/BaseController';
import { FragmentNames, ReverseMedicationAliases } from '../../../../codegen';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ApplicationRoute } from '../../../../constants/routes';
import { AdminModalsService } from '../../services/admin-modals-service';
import { PatientProfile } from './medication-viewer-types';
import TimelineContext, { PrescriptionType } from '../../services/timeline-service';

@Controller({ component: ReverseMedicationAliases.LAYOUT_REVERSE_MEDICATION, fragment: FragmentNames.REVERSE_MEDICATION })
export default class ReverseMedicationController extends BaseController {

  @Inject(ReverseMedicationAliases.AUTOCOMPLETE_REVERSE_MEDICATION_REASONS)
  private reasonAutocompleteApi!: AutocompleteAPI;

  @Inject(ReverseMedicationAliases.FIELD_REVERSE_MEDICATION_JUSTIFICATION)
  private justificationField!: FieldAPI;

  @OnMount(ReverseMedicationAliases.FETCH_REVERSE_MEDICATION_REASONS)
  handleFetchReasonMount(event: MountEvent): void {
    const medicationItemType = PrescriptionType.MEDICINE;
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: medicationItemType }, { skip: true });
  }

  @OnMount(ReverseMedicationAliases.FIELD_REVERSE_MEDICATION_JUSTIFICATION)
  handleMountJustificationField({ target }: EventData<FieldAPI>): void {
    const adepParam6Value = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_REVERSING_TIME_MANAGEMENT);
    const isMandatoryJustification = adepParam6Value === PatientProfile.MANDATORY_JUSTIFICATION
    if (isMandatoryJustification) {
      const requiredValidatior = { name: 'required' }
      target.setValidators([requiredValidatior])
    }
  }

  @OnBeforeCancel(ReverseMedicationAliases.FORM_REVERSE_MEDICATION)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnBeforeSubmit(ReverseMedicationAliases.FORM_REVERSE_MEDICATION)
  handleSubmitForm(): void {
    this.reverseMedication();
  }

  reverseMedication(): void {
    const scheduleSequence = this.getHistoryRouteParam<number>('scheduleSequenceNumber');
    const encounterNumber = this.getHistoryRouteParam<number>('encounterId');
    const paramTasyOrTws22 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY);
    const quantityHourPrescription: number = paramTasyOrTws22 ? +paramTasyOrTws22 : 0;
    const reason = this.reasonAutocompleteApi.getValue() as unknown as Record<string, Value>;
    const reasonId = reason?.sequenceNumber || null;
    const justification = this.justificationField.getValue() as unknown as string;
    const itemType = TimelineContext.get().itemType;

    const body = {
      currentDate: moment().format(ISO_FULL_DATE_FORMAT),
      encounterNumber,
      itemType,
      justification,
      quantityHourPrescription,
      reasonId,
      scheduleSequence,
    }

    HttpService.post(
      Endpoint.REVERSE_ADMINISTRATION_MEDICATION, {}, { body }
    ).then(response => {
      if (response != null) {
        this.goToTimeline();
        this.showToastSucessReverseAsministration();
      }
    }).catch((err) => {
      const message = err.body.messageDescription;
      if (message != null) {
        this.showToastErrorReverseAsministration(message);
      } else {
        this.showToastErrorReverseAsministration(err.body.errors[0]);
      }
    });
  }

  private showToastErrorReverseAsministration(value: string): void {
    return ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'show-revert-error',
      level: ToastLevel.ERROR,
      message: value,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  private showToastSucessReverseAsministration(): void {
    return ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'show-sucess-reverse',
      level: ToastLevel.SUCCESS,
      message: 'msg.revert-medication',
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  private goToTimeline(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
      encounterId,
      patientId,
    });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.REVERSE_MEDICATION);
    AdminModalsService.redirectToTimeline(FragmentNames.MEDICATION_VIEWER);
  }
}
